import * as types from '../../lib/actionTypes.es6'

export const initialState = {
  products: [],
  errors: null,
  loader: false,
  currentProduct: null,
  currentProductPackage: null,
  currentProductPackageBenefits: [],

  createdProduct: null,
  productSuccess: false,
  productLoader: false,
  productError: null,

  createdProductPackage: null,
  productPackageSuccess: false,
  productPackageLoader: false,
  productPackageError: null,

  createdProductPackageBenefit: null,
  productPackageBenefitSuccess: false,
  productPackageBenefitLoader: false,
  productPackageBenefitError: null,

  dialogProductType: '',

  editedProduct: null,

  createdProductAddon: null,
  productAddonSuccess: false,
  productAddonLoader: false,
  productAddonError: null,

  cashBackConfigSuccess: false,
  cashBackConfigLoader: false,
  cashBackConfigError: null,

  ageValidationRuleSuccess: false,
  ageValidationRuleLoader: false,
  ageValidationRuleError: null,

  productAutoRenewalConfigSuccess: false,
  productAutoRenewalConfigLoader: false,
  productAutoRenewalConfigError: null,

  // Rating Nodes
  ratingNodes: [],
  ratingNodeLoader: false,
  ratingNodeError: null,
  ratingNodeSuccess: false,

  // Rating Tables (partner-scoped)
  ratingTables: [],
  ratingTablesLoader: false,
  ratingTablesError: null,
  ratingTableActionLoader: false,
  ratingTableActionSuccess: null,
  ratingTableActionError: null,
  ratingTableSaveLoader: false,
  ratingTableSaveChangeRequestGuid: null,

  // Benefit pricing wizard (Cover/Waiting Period/Premium "per rule" chains —
  // see docs/2026-09-04-benefit-pricing-wizard-design.md)
  benefitRatingChainLoader: false,
  benefitRatingChainError: null,
  benefitRatingChainSuccess: false,
  benefitRatingChainProgress: null,

  // Benefit pricing wizard — edit mode (§9)
  updateBenefitRatingChainLoader: false,
  updateBenefitRatingChainError: null,
  updateBenefitRatingChainSuccess: false,
  updateBenefitRatingChainPending: null, // [{key, changeRequestGuid}] — set when an "update existing table" call came back 202
  updateBenefitRatingChainProgress: null,

  // Rating Bulk Import (workbook → rating tables + nodes, one product at a time)
  ratingImportProductGuid: null, // which product this state belongs to — lets
  // the panel tell "still the same product, just remounted by a tab switch"
  // apart from "navigated to a different product" without an unmount-keyed effect
  ratingImportTemplateLoader: false,
  ratingImportTemplateError: null,
  ratingImportWorkbook: null, // the selected File, re-used for validate + import
  ratingImportValidateLoader: false,
  ratingImportValidateError: null,
  ratingImportReport: null, // dry-run RatingImportResultDto (no change_request_guid)
  ratingImportSubmitLoader: false,
  ratingImportSubmitError: null,
  ratingImportResult: null, // real import result — has change_request_guid

  // Rating Snapshots (rating history, versioned apart from the product)
  ratingSnapshots: [],
  ratingSnapshotsLoader: false,
  ratingSnapshotsError: null,

  // Change Requests (partner-wide)
  changeRequests: [],
  changeRequestsLoader: false,
  changeRequestsError: null,
  changeRequestActionLoader: false,
  changeRequestActionSuccess: null,
  changeRequestActionError: null,

  // AI Drafting
  draftFile: null,
  uploadDocumentLoader: false,
  uploadDocumentError: null,
  draftJobId: null,
  draftPollAttempts: 0, // incremented per still-processing poll; drives the next poll tick
  pollDraftJobLoader: false,
  pollDraftJobError: null,
  draftResult: null, // { draft_product, confidence, open_questions, excluded }

  // Drafts table — list of every draft generated for the partner
  partnerDrafts: [],
  partnerDraftsLoader: false,
  partnerDraftsError: null,

  // Draft review + approve-to-import
  reviewDraft: null, // full draft detail being reviewed { job_id, result, ... }
  reviewDraftLoader: false,
  reviewDraftError: null,
  importDraftLoader: false,
  importDraftError: null,
  importedDraftProduct: null, // product created from an approved draft

  quotationResults: null,
  quotationLoader: false,
  quotationError: null
}

export default function genericProductsReducer(state = initialState, action) {
  switch (action.type) {
    case types.VIEW_ALL_PRODUCTS_REQUEST:
      return { ...state, errors: null, loader: true, products: [] }

    case types.VIEW_ALL_PRODUCTS_SUCCESS:
      return {
        ...state,
        products: action.payload,
        loader: false,
        errors: null
      }

    case types.VIEW_ALL_PRODUCTS_FAILURE:
      return { ...state, loader: false, errors: action.payload }

    case types.CREATE_PRODUCT_REQUEST:
      return {
        ...state,
        productError: null,
        productLoader: true,
        productSuccess: false,
        createdProduct: null
      }

    case types.CREATE_PRODUCT_SUCCESS:
      return {
        ...state,
        createdProduct: action.payload,
        productSuccess: true,
        productLoader: false,
        productError: null
      }

    case types.VIEW_PRODUCT_REQUEST:
      return { ...state, errors: null, loader: true }

    case types.VIEW_PRODUCT_SUCCESS:
      return {
        ...state,
        currentProduct: action.payload,
        loader: false,
        errors: null
      }

    case types.VIEW_PRODUCT_FAILURE:
      return { ...state, loader: false, errors: action.payload }

    case types.CREATE_PRODUCT_FAILURE:
      return { ...state, productLoader: false, productError: action.payload }

    case types.EDIT_PRODUCT_REQUEST:
      return {
        ...state,
        productError: null,
        productLoader: true,
        productSuccess: false
      }

    case types.EDIT_PRODUCT_SUCCESS:
      return {
        ...state,
        editedProduct: action.payload,
        productSuccess: true,
        productLoader: false,
        productError: null
      }

    case types.EDIT_PRODUCT_FAILURE:
      return { ...state, productLoader: false, productError: action.payload }

    case types.RESET_PRODUCT_SUCCESS:
      return { ...state, productSuccess: false }

    case types.RESET_PRODUCT_ERROR:
      return { ...state, productError: null }

    case types.CREATE_PRODUCT_PACKAGE_REQUEST:
      return {
        ...state,
        productPackageError: null,
        productPackageLoader: true,
        productPackageSuccess: false,
        createdProductPackage: null
      }

    case types.CREATE_PRODUCT_PACKAGE_SUCCESS:
      return {
        ...state,
        createdProductPackage: action.payload,
        productPackageSuccess: true,
        productPackageLoader: false,
        productPackageError: null
      }

    case types.CREATE_PRODUCT_PACKAGE_FAILURE:
      return {
        ...state,
        productPackageLoader: false,
        productPackageError: action.payload
      }

    case types.RESET_CREATE_PRODUCT_PACKAGE_SUCCESS:
      return { ...state, productPackageSuccess: false }

    case types.RESET_CREATE_PRODUCT_PACKAGE_ERROR:
      return { ...state, productPackageError: null }

    case types.UPDATE_PRODUCT_PACKAGE_REQUEST:
      return {
        ...state,
        productPackageError: null,
        productPackageLoader: true,
        productPackageSuccess: false
      }

    case types.UPDATE_PRODUCT_PACKAGE_SUCCESS:
      return {
        ...state,
        productPackageSuccess: true,
        productPackageLoader: false,
        productPackageError: null
      }

    case types.UPDATE_PRODUCT_PACKAGE_FAILURE:
      return {
        ...state,
        productPackageLoader: false,
        productPackageError: action.payload
      }

    case types.DELETE_PRODUCT_PACKAGE_REQUEST:
      return {
        ...state,
        productPackageError: null,
        productPackageLoader: true,
        productPackageSuccess: false
      }

    case types.DELETE_PRODUCT_PACKAGE_SUCCESS:
      return {
        ...state,
        productPackageSuccess: true,
        productPackageLoader: false,
        productPackageError: null
      }

    case types.DELETE_PRODUCT_PACKAGE_FAILURE:
      return {
        ...state,
        productPackageLoader: false,
        productPackageError: action.payload
      }

    case types.RESET_UPDATE_PRODUCT_PACKAGE_SUCCESS:
      return { ...state, productPackageSuccess: false }

    case types.RESET_DELETE_PRODUCT_PACKAGE_SUCCESS:
      return { ...state, productPackageSuccess: false }

    case types.CREATE_PRODUCT_PACKAGE_BENEFITS_REQUEST:
      return {
        ...state,
        productPackageBenefitError: null,
        productPackageBenefitLoader: true,
        productPackageBenefitSuccess: false,
        createdProductPackageBenefit: null
      }

    case types.CREATE_PRODUCT_PACKAGE_BENEFITS_SUCCESS:
      return {
        ...state,
        createdProductPackageBenefit: action.payload,
        productPackageBenefitSuccess: true,
        productPackageBenefitLoader: false,
        productPackageBenefitError: null
      }

    case types.CREATE_PRODUCT_PACKAGE_BENEFITS_FAILURE:
      return {
        ...state,
        productPackageBenefitLoader: false,
        productPackageBenefitError: action.payload
      }

    case types.UPDATE_PRODUCT_PACKAGE_BENEFITS_REQUEST:
      return {
        ...state,
        productPackageBenefitError: null,
        productPackageBenefitLoader: true,
        productPackageBenefitSuccess: false
      }

    case types.UPDATE_PRODUCT_PACKAGE_BENEFITS_SUCCESS:
      return {
        ...state,
        productPackageBenefitSuccess: true,
        productPackageBenefitLoader: false,
        productPackageBenefitError: null
      }

    case types.UPDATE_PRODUCT_PACKAGE_BENEFITS_FAILURE:
      return {
        ...state,
        productPackageBenefitLoader: false,
        productPackageBenefitError: action.payload
      }

    case types.DELETE_PRODUCT_PACKAGE_BENEFITS_REQUEST:
      return {
        ...state,
        productPackageBenefitError: null,
        productPackageBenefitLoader: true,
        productPackageBenefitSuccess: false
      }

    case types.DELETE_PRODUCT_PACKAGE_BENEFITS_SUCCESS:
      return {
        ...state,
        productPackageBenefitSuccess: true,
        productPackageBenefitLoader: false,
        productPackageBenefitError: null
      }

    case types.DELETE_PRODUCT_PACKAGE_BENEFITS_FAILURE:
      return {
        ...state,
        productPackageBenefitLoader: false,
        productPackageBenefitError: action.payload
      }

    case types.RESET_PRODUCT_PACKAGE_BENEFITS_SUCCESS:
      return { ...state, productPackageBenefitSuccess: false }

    case types.RESET_PRODUCT_PACKAGE_BENEFITS_ERROR:
      return {
        ...state,
        productPackageBenefitError: null,
        productPackageBenefitLoader: false
      }

    case types.CREATE_PRODUCT_ADDON_REQUEST:
      return {
        ...state,
        productAddonError: null,
        productAddonLoader: true,
        productAddonSuccess: false,
        createdProductAddon: null
      }

    case types.CREATE_PRODUCT_ADDON_SUCCESS:
      return {
        ...state,
        createdProductAddon: action.payload,
        productAddonSuccess: true,
        productAddonLoader: false,
        productAddonError: null
      }

    case types.CREATE_PRODUCT_ADDON_FAILURE:
      return {
        ...state,
        productAddonLoader: false,
        productAddonError: action.payload
      }

    case types.RESET_CREATE_PRODUCT_ADDON_SUCCESS:
      return { ...state, productAddonSuccess: false }

    case types.RESET_CREATE_PRODUCT_ADDON_ERROR:
      return { ...state, productAddonError: null }

    case types.GET_PRODUCT_ADDONS_REQUEST:
      return {
        ...state,
        productAddonError: null,
        productAddonLoader: true,
        productAddonSuccess: false
      }

    case types.GET_PRODUCT_ADDONS_SUCCESS:
      return {
        ...state,
        productAddonSuccess: true,
        productAddonLoader: false,
        productAddonError: null
      }

    case types.GET_PRODUCT_ADDONS_FAILURE:
      return {
        ...state,
        productAddonLoader: false,
        productAddonError: action.payload
      }

    case types.DELETE_PRODUCT_ADDON_REQUEST:
      return {
        ...state,
        productAddonError: null,
        productAddonLoader: true,
        productAddonSuccess: false
      }

    case types.DELETE_PRODUCT_ADDON_SUCCESS:
      return {
        ...state,
        productAddonSuccess: true,
        productAddonLoader: false,
        productAddonError: null
      }

    case types.DELETE_PRODUCT_ADDON_FAILURE:
      return {
        ...state,
        productAddonLoader: false,
        productAddonError: action.payload
      }

    case types.UPDATE_PRODUCT_ADDON_REQUEST:
      return {
        ...state,
        productAddonError: null,
        productAddonLoader: true,
        productAddonSuccess: false
      }

    case types.UPDATE_PRODUCT_ADDON_SUCCESS:
      return {
        ...state,
        productAddonSuccess: true,
        productAddonLoader: false,
        productAddonError: null
      }

    case types.UPDATE_PRODUCT_ADDON_FAILURE:
      return {
        ...state,
        productAddonLoader: false,
        productAddonError: action.payload
      }

    case types.CREATE_CASHBACK_CONFIG_REQUEST:
      return {
        ...state,
        cashBackConfigError: null,
        cashBackConfigLoader: true,
        cashBackConfigSuccess: false
      }

    case types.CREATE_CASHBACK_CONFIG_SUCCESS:
      return {
        ...state,
        cashBackConfigSuccess: true,
        cashBackConfigLoader: false,
        cashBackConfigError: null
      }

    case types.CREATE_CASHBACK_CONFIG_FAILURE:
      return {
        ...state,
        cashBackConfigLoader: false,
        cashBackConfigError: action.payload
      }

    case types.RESET_CASHBACK_CONFIG_SUCCESS:
      return { ...state, cashBackConfigSuccess: false }

    case types.RESET_CASHBACK_CONFIG_ERROR:
      return { ...state, cashBackConfigError: null }

    case types.UPDATE_CASHBACK_CONFIG_REQUEST:
      return {
        ...state,
        cashBackConfigError: null,
        cashBackConfigLoader: true,
        cashBackConfigSuccess: false
      }

    case types.UPDATE_CASHBACK_CONFIG_SUCCESS:
      return {
        ...state,
        cashBackConfigSuccess: true,
        cashBackConfigLoader: false,
        cashBackConfigError: null
      }

    case types.UPDATE_CASHBACK_CONFIG_FAILURE:
      return {
        ...state,
        cashBackConfigLoader: false,
        cashBackConfigError: action.payload
      }

    case types.DELETE_CASHBACK_CONFIG_REQUEST:
      return {
        ...state,
        cashBackConfigError: null,
        cashBackConfigLoader: true,
        cashBackConfigSuccess: false
      }

    case types.DELETE_CASHBACK_CONFIG_SUCCESS:
      return {
        ...state,
        cashBackConfigSuccess: true,
        cashBackConfigLoader: false,
        cashBackConfigError: null
      }

    case types.DELETE_CASHBACK_CONFIG_FAILURE:
      return {
        ...state,
        cashBackConfigLoader: false,
        cashBackConfigError: action.payload
      }

    case types.GET_CASHBACK_CONFIGS_REQUEST:
      return {
        ...state,
        cashBackConfigError: null,
        cashBackConfigLoader: true,
        cashBackConfigSuccess: false
      }

    case types.GET_CASHBACK_CONFIGS_SUCCESS:
      return {
        ...state,
        cashBackConfigSuccess: true,
        cashBackConfigLoader: false,
        cashBackConfigError: null
      }

    case types.GET_CASHBACK_CONFIGS_FAILURE:
      return {
        ...state,
        cashBackConfigLoader: false,
        cashBackConfigError: action.payload
      }

    case types.CREATE_AGE_VALIDATION_RULE_REQUEST:
      return {
        ...state,
        ageValidationRuleError: null,
        ageValidationRuleLoader: true,
        ageValidationRuleSuccess: false
      }

    case types.CREATE_AGE_VALIDATION_RULE_SUCCESS:
      return {
        ...state,
        ageValidationRuleSuccess: true,
        ageValidationRuleLoader: false,
        ageValidationRuleError: null
      }

    case types.CREATE_AGE_VALIDATION_RULE_FAILURE:
      return {
        ...state,
        ageValidationRuleLoader: false,
        ageValidationRuleError: action.payload
      }

    case types.RESET_AGE_VALIDATION_RULE_SUCCESS:
      return { ...state, ageValidationRuleSuccess: false }

    case types.RESET_AGE_VALIDATION_RULE_ERROR:
      return { ...state, ageValidationRuleError: null }

    case types.UPDATE_AGE_VALIDATION_RULE_REQUEST:
      return {
        ...state,
        ageValidationRuleError: null,
        ageValidationRuleLoader: true,
        ageValidationRuleSuccess: false
      }

    case types.UPDATE_AGE_VALIDATION_RULE_SUCCESS:
      return {
        ...state,
        ageValidationRuleSuccess: true,
        ageValidationRuleLoader: false,
        ageValidationRuleError: null
      }

    case types.UPDATE_AGE_VALIDATION_RULE_FAILURE:
      return {
        ...state,
        ageValidationRuleLoader: false,
        ageValidationRuleError: action.payload
      }

    case types.DELETE_AGE_VALIDATION_RULE_REQUEST:
      return {
        ...state,
        ageValidationRuleError: null,
        ageValidationRuleLoader: true,
        ageValidationRuleSuccess: false
      }

    case types.DELETE_AGE_VALIDATION_RULE_SUCCESS:
      return {
        ...state,
        ageValidationRuleSuccess: true,
        ageValidationRuleLoader: false,
        ageValidationRuleError: null
      }

    case types.DELETE_AGE_VALIDATION_RULE_FAILURE:
      return {
        ...state,
        ageValidationRuleLoader: false,
        ageValidationRuleError: action.payload
      }

    case types.GET_AGE_VALIDATION_RULES_REQUEST:
      return {
        ...state,
        ageValidationRuleError: null,
        ageValidationRuleLoader: true,
        ageValidationRuleSuccess: false
      }

    case types.GET_AGE_VALIDATION_RULES_SUCCESS:
      return {
        ...state,
        ageValidationRuleSuccess: true,
        ageValidationRuleLoader: false,
        ageValidationRuleError: null
      }

    case types.GET_AGE_VALIDATION_RULES_FAILURE:
      return {
        ...state,
        ageValidationRuleLoader: false,
        ageValidationRuleError: action.payload
      }

    case types.CREATE_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST:
      return {
        ...state,
        productAutoRenewalConfigError: null,
        productAutoRenewalConfigLoader: true,
        productAutoRenewalConfigSuccess: false
      }

    case types.CREATE_PRODUCT_AUTO_RENEWAL_CONFIG_SUCCESS:
      return {
        ...state,
        productAutoRenewalConfigSuccess: true,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: null
      }

    case types.CREATE_PRODUCT_AUTO_RENEWAL_CONFIG_FAILURE:
      return {
        ...state,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: action.payload
      }

    case types.UPDATE_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST:
      return {
        ...state,
        productAutoRenewalConfigError: null,
        productAutoRenewalConfigLoader: true,
        productAutoRenewalConfigSuccess: false
      }

    case types.UPDATE_PRODUCT_AUTO_RENEWAL_CONFIG_SUCCESS:
      return {
        ...state,
        productAutoRenewalConfigSuccess: true,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: null
      }

    case types.UPDATE_PRODUCT_AUTO_RENEWAL_CONFIG_FAILURE:
      return {
        ...state,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: action.payload
      }

    case types.DELETE_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST:
      return {
        ...state,
        productAutoRenewalConfigError: null,
        productAutoRenewalConfigLoader: true,
        productAutoRenewalConfigSuccess: false
      }

    case types.DELETE_PRODUCT_AUTO_RENEWAL_CONFIG_SUCCESS:
      return {
        ...state,
        productAutoRenewalConfigSuccess: true,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: null
      }

    case types.DELETE_PRODUCT_AUTO_RENEWAL_CONFIG_FAILURE:
      return {
        ...state,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: action.payload
      }

    case types.GET_PRODUCT_AUTO_RENEWAL_CONFIG_REQUEST:
      return {
        ...state,
        productAutoRenewalConfigError: null,
        productAutoRenewalConfigLoader: true,
        productAutoRenewalConfigSuccess: false
      }
    case types.GET_PRODUCT_AUTO_RENEWAL_CONFIG_SUCCESS:
      return {
        ...state,
        productAutoRenewalConfigSuccess: true,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: null
      }

    case types.GET_PRODUCT_AUTO_RENEWAL_CONFIG_FAILURE:
      return {
        ...state,
        productAutoRenewalConfigLoader: false,
        productAutoRenewalConfigError: action.payload
      }

    case types.RESET_PRODUCT_AUTO_RENEWAL_CONFIG_SUCCESS:
      return { ...state, productAutoRenewalConfigSuccess: false }

    case types.RESET_PRODUCT_AUTO_RENEWAL_CONFIG_ERROR:
      return { ...state, productAutoRenewalConfigError: null }

    // AI Drafting cases
    case types.DRAFT_FILE_SELECT:
      return { ...state, draftFile: action.payload }

    case types.DRAFT_FILE_CLEAR:
      return { ...state, draftFile: null }

    case types.UPLOAD_DOCUMENT_REQUEST:
      return { ...state, uploadDocumentLoader: true, uploadDocumentError: null }

    case types.UPLOAD_DOCUMENT_SUCCESS:
      return {
        ...state,
        uploadDocumentLoader: false,
        draftJobId: action.payload,
        draftPollAttempts: 0,
        draftResult: null,
        pollDraftJobError: null
      }

    case types.UPLOAD_DOCUMENT_FAILURE:
      return {
        ...state,
        uploadDocumentLoader: false,
        uploadDocumentError: action.payload
      }

    case types.POLL_DRAFT_JOB_REQUEST:
      return { ...state, pollDraftJobLoader: true, pollDraftJobError: null }

    case types.POLL_DRAFT_JOB_SUCCESS:
      return {
        ...state,
        pollDraftJobLoader: false,
        draftResult: action.payload
      }

    // Job accepted but not finished — bump the attempt counter so the dialog
    // schedules the next poll. NOT a failure.
    case types.POLL_DRAFT_JOB_PROCESSING:
      return {
        ...state,
        pollDraftJobLoader: false,
        draftPollAttempts: state.draftPollAttempts + 1
      }

    case types.POLL_DRAFT_JOB_FAILURE:
      return {
        ...state,
        pollDraftJobLoader: false,
        pollDraftJobError: action.payload
      }

    case types.RESET_DRAFT_STATE:
      return {
        ...state,
        draftFile: null,
        draftJobId: null,
        draftPollAttempts: 0,
        draftResult: null,
        uploadDocumentError: null,
        pollDraftJobError: null
      }

    case types.FETCH_PARTNER_DRAFTS_REQUEST:
      return { ...state, partnerDraftsLoader: true, partnerDraftsError: null }

    case types.FETCH_PARTNER_DRAFTS_SUCCESS:
      return {
        ...state,
        partnerDraftsLoader: false,
        partnerDrafts: action.payload
      }

    case types.FETCH_PARTNER_DRAFTS_FAILURE:
      return {
        ...state,
        partnerDraftsLoader: false,
        partnerDraftsError: action.payload
      }

    case types.REVIEW_DRAFT_REQUEST:
      return {
        ...state,
        reviewDraftLoader: true,
        reviewDraftError: null,
        importDraftError: null,
        importedDraftProduct: null
      }

    case types.REVIEW_DRAFT_SUCCESS:
      return {
        ...state,
        reviewDraftLoader: false,
        reviewDraft: action.payload,
        // Populate currentProduct so the redux-connected info tab renders the
        // draft; sub-entity tabs read from the draft object passed as props.
        currentProduct:
          (action.payload &&
            action.payload.result &&
            action.payload.result.draft_product) ||
          null
      }

    case types.REVIEW_DRAFT_FAILURE:
      return {
        ...state,
        reviewDraftLoader: false,
        reviewDraftError: action.payload
      }

    case types.CLEAR_REVIEW_DRAFT:
      return {
        ...state,
        reviewDraft: null,
        reviewDraftError: null,
        importDraftError: null,
        importedDraftProduct: null
      }

    case types.IMPORT_DRAFT_PRODUCT_REQUEST:
      return { ...state, importDraftLoader: true, importDraftError: null }

    case types.IMPORT_DRAFT_PRODUCT_SUCCESS:
      return {
        ...state,
        importDraftLoader: false,
        importedDraftProduct: action.payload
      }

    case types.IMPORT_DRAFT_PRODUCT_FAILURE:
      return {
        ...state,
        importDraftLoader: false,
        importDraftError: action.payload
      }

    case types.EVALUATE_PRODUCT_QUOTE_REQUEST:
    case types.COMPARE_QUOTATIONS_REQUEST:
      return {
        ...state,
        quotationLoader: true,
        quotationError: null,
        quotationResults: null
      }

    case types.EVALUATE_PRODUCT_QUOTE_SUCCESS:
    case types.COMPARE_QUOTATIONS_SUCCESS:
      return {
        ...state,
        quotationLoader: false,
        quotationResults: action.payload
      }

    case types.EVALUATE_PRODUCT_QUOTE_FAILURE:
    case types.COMPARE_QUOTATIONS_FAILURE:
      return {
        ...state,
        quotationLoader: false,
        quotationError: action.payload
      }

    case types.RESET_QUOTATION_RESULTS:
      return { ...state, quotationResults: null, quotationError: null }

    case types.GET_PRODUCT_RATING_NODES_REQUEST:
      return { ...state, ratingNodeLoader: true, ratingNodeError: null }

    case types.GET_PRODUCT_RATING_NODES_SUCCESS:
      return { ...state, ratingNodeLoader: false, ratingNodes: action.payload }

    case types.GET_PRODUCT_RATING_NODES_FAILURE:
      return {
        ...state,
        ratingNodeLoader: false,
        ratingNodeError: action.payload
      }

    case types.CREATE_PRODUCT_RATING_NODE_REQUEST:
      return {
        ...state,
        ratingNodeLoader: true,
        ratingNodeError: null,
        ratingNodeSuccess: false
      }

    case types.CREATE_PRODUCT_RATING_NODE_SUCCESS:
      return {
        ...state,
        ratingNodeLoader: false,
        ratingNodeSuccess: true,
        ratingNodes: [...state.ratingNodes, action.payload]
      }

    case types.CREATE_PRODUCT_RATING_NODE_FAILURE:
      return {
        ...state,
        ratingNodeLoader: false,
        ratingNodeError: action.payload
      }

    case types.UPDATE_PRODUCT_RATING_NODE_REQUEST:
      return {
        ...state,
        ratingNodeLoader: true,
        ratingNodeError: null,
        ratingNodeSuccess: false
      }

    case types.UPDATE_PRODUCT_RATING_NODE_SUCCESS:
      return {
        ...state,
        ratingNodeLoader: false,
        ratingNodeSuccess: true,
        ratingNodes: state.ratingNodes.map(n =>
          n.guid === action.payload.guid ? action.payload : n
        )
      }

    case types.UPDATE_PRODUCT_RATING_NODE_FAILURE:
      return {
        ...state,
        ratingNodeLoader: false,
        ratingNodeError: action.payload
      }

    case types.DELETE_PRODUCT_RATING_NODE_REQUEST:
      return {
        ...state,
        ratingNodeLoader: true,
        ratingNodeError: null,
        ratingNodeSuccess: false
      }

    case types.DELETE_PRODUCT_RATING_NODE_SUCCESS:
      return {
        ...state,
        ratingNodeLoader: false,
        ratingNodeSuccess: true,
        ratingNodes: state.ratingNodes.filter(n => n.guid !== action.payload)
      }

    case types.DELETE_PRODUCT_RATING_NODE_FAILURE:
      return {
        ...state,
        ratingNodeLoader: false,
        ratingNodeError: action.payload
      }

    case types.RESET_RATING_NODE_SUCCESS:
      return { ...state, ratingNodeSuccess: false }

    case types.RESET_RATING_NODE_ERROR:
      return { ...state, ratingNodeError: null }

    // ── Rating Tables ──────────────────────────────────────────────────────
    case types.GET_RATING_TABLES_REQUEST:
      return { ...state, ratingTablesLoader: true, ratingTablesError: null }
    case types.GET_RATING_TABLES_SUCCESS:
      return {
        ...state,
        ratingTablesLoader: false,
        ratingTables: action.payload || []
      }
    case types.GET_RATING_TABLES_FAILURE:
      return {
        ...state,
        ratingTablesLoader: false,
        ratingTablesError: action.payload
      }
    case types.RATING_TABLE_ACTION_REQUEST:
      return {
        ...state,
        ratingTableActionLoader: true,
        ratingTableActionError: null,
        ratingTableActionSuccess: null
      }
    case types.RATING_TABLE_ACTION_SUCCESS:
      return {
        ...state,
        ratingTableActionLoader: false,
        ratingTableActionSuccess: action.payload || 'done'
      }
    case types.RATING_TABLE_ACTION_FAILURE:
      return {
        ...state,
        ratingTableActionLoader: false,
        ratingTableActionError: action.payload
      }
    case types.SAVE_RATING_TABLE_REQUEST:
      return {
        ...state,
        ratingTableSaveLoader: true,
        ratingTableActionError: null,
        ratingTableActionSuccess: null
      }
    case types.SAVE_RATING_TABLE_SUCCESS:
      // Creating a table is unconditionally exempt from the approval gate
      // (product-configuration#27) and applies immediately; editing an
      // existing one still respects product_maker_checker and can come back
      // pending — action.payload.pending (from handleMutationResponse) says
      // which actually happened, so "saved" never misreports a pending change.
      return {
        ...state,
        ratingTableSaveLoader: false,
        ratingTableActionSuccess: action.payload.pending
          ? 'save-pending'
          : 'saved',
        ratingTableSaveChangeRequestGuid: action.payload.changeRequestGuid
      }
    case types.SAVE_RATING_TABLE_FAILURE:
      return {
        ...state,
        ratingTableSaveLoader: false,
        ratingTableActionError: action.payload
      }
    case types.RESET_RATING_TABLE_FEEDBACK:
      return {
        ...state,
        ratingTableActionSuccess: null,
        ratingTableActionError: null,
        ratingTableSaveChangeRequestGuid: null
      }

    // ── Benefit pricing wizard ────────────────────────────────────────────────
    case types.CREATE_BENEFIT_RATING_CHAIN_REQUEST:
      return {
        ...state,
        benefitRatingChainLoader: true,
        benefitRatingChainError: null,
        benefitRatingChainSuccess: false
      }
    case types.CREATE_BENEFIT_RATING_CHAIN_PROGRESS:
      return {
        ...state,
        benefitRatingChainProgress: action.payload
      }
    case types.CREATE_BENEFIT_RATING_CHAIN_SUCCESS:
      return {
        ...state,
        benefitRatingChainLoader: false,
        benefitRatingChainSuccess: true,
        benefitRatingChainProgress: null
      }
    case types.CREATE_BENEFIT_RATING_CHAIN_FAILURE:
      return {
        ...state,
        benefitRatingChainLoader: false,
        benefitRatingChainError: action.payload
      }
    case types.RESET_BENEFIT_RATING_CHAIN_STATE:
      return {
        ...state,
        benefitRatingChainLoader: false,
        benefitRatingChainError: null,
        benefitRatingChainSuccess: false,
        benefitRatingChainProgress: null
      }

    // ── Benefit pricing wizard — edit mode (§9) ─────────────────────────────
    case types.UPDATE_BENEFIT_RATING_CHAIN_REQUEST:
      return {
        ...state,
        updateBenefitRatingChainLoader: true,
        updateBenefitRatingChainError: null,
        updateBenefitRatingChainSuccess: false,
        updateBenefitRatingChainPending: null
      }
    case types.UPDATE_BENEFIT_RATING_CHAIN_PROGRESS:
      return {
        ...state,
        updateBenefitRatingChainProgress: action.payload
      }
    case types.UPDATE_BENEFIT_RATING_CHAIN_SUCCESS:
      return {
        ...state,
        updateBenefitRatingChainLoader: false,
        updateBenefitRatingChainSuccess: true,
        updateBenefitRatingChainProgress: null,
        updateBenefitRatingChainPending: (action.payload && action.payload.pending) || null
      }
    case types.UPDATE_BENEFIT_RATING_CHAIN_FAILURE:
      return {
        ...state,
        updateBenefitRatingChainLoader: false,
        updateBenefitRatingChainError: action.payload
      }
    case types.RESET_UPDATE_BENEFIT_RATING_CHAIN_STATE:
      return {
        ...state,
        updateBenefitRatingChainLoader: false,
        updateBenefitRatingChainError: null,
        updateBenefitRatingChainSuccess: false,
        updateBenefitRatingChainProgress: null,
        updateBenefitRatingChainPending: null
      }

    // ── Rating Bulk Import ──────────────────────────────────────────────────
    case types.DOWNLOAD_RATING_TEMPLATE_REQUEST:
      return {
        ...state,
        ratingImportTemplateLoader: true,
        ratingImportTemplateError: null
      }
    case types.DOWNLOAD_RATING_TEMPLATE_SUCCESS:
      return { ...state, ratingImportTemplateLoader: false }
    case types.DOWNLOAD_RATING_TEMPLATE_FAILURE:
      return {
        ...state,
        ratingImportTemplateLoader: false,
        ratingImportTemplateError: action.payload
      }
    case types.SELECT_RATING_WORKBOOK:
      // A newly chosen file invalidates any prior dry-run/submit result.
      // Stamps ratingImportProductGuid so the panel can tell "still the same
      // product, just remounted by a tab switch" apart from "different product".
      return {
        ...state,
        ratingImportProductGuid: action.payload.productGuid,
        ratingImportWorkbook: action.payload.file,
        ratingImportReport: null,
        ratingImportValidateError: null,
        ratingImportResult: null,
        ratingImportSubmitError: null
      }
    case types.VALIDATE_RATING_WORKBOOK_REQUEST:
      return {
        ...state,
        ratingImportValidateLoader: true,
        ratingImportValidateError: null,
        ratingImportReport: null
      }
    case types.VALIDATE_RATING_WORKBOOK_SUCCESS:
      return {
        ...state,
        ratingImportValidateLoader: false,
        ratingImportReport: action.payload
      }
    case types.VALIDATE_RATING_WORKBOOK_FAILURE:
      return {
        ...state,
        ratingImportValidateLoader: false,
        ratingImportValidateError: action.payload
      }
    case types.IMPORT_RATING_WORKBOOK_REQUEST:
      return {
        ...state,
        ratingImportSubmitLoader: true,
        ratingImportSubmitError: null
      }
    case types.IMPORT_RATING_WORKBOOK_SUCCESS:
      return {
        ...state,
        ratingImportSubmitLoader: false,
        ratingImportResult: action.payload
      }
    case types.IMPORT_RATING_WORKBOOK_FAILURE:
      return {
        ...state,
        ratingImportSubmitLoader: false,
        ratingImportSubmitError: action.payload
      }
    case types.RESET_RATING_IMPORT:
      return {
        ...state,
        ratingImportProductGuid: null,
        ratingImportWorkbook: null,
        ratingImportReport: null,
        ratingImportValidateError: null,
        ratingImportResult: null,
        ratingImportSubmitError: null
      }

    // ── Rating Snapshots ────────────────────────────────────────────────────
    case types.GET_RATING_SNAPSHOTS_REQUEST:
      return {
        ...state,
        ratingSnapshotsLoader: true,
        ratingSnapshotsError: null
      }
    case types.GET_RATING_SNAPSHOTS_SUCCESS:
      return {
        ...state,
        ratingSnapshotsLoader: false,
        ratingSnapshots: action.payload || []
      }
    case types.GET_RATING_SNAPSHOTS_FAILURE:
      return {
        ...state,
        ratingSnapshotsLoader: false,
        ratingSnapshotsError: action.payload
      }

    // ── Change Requests ────────────────────────────────────────────────────
    case types.GET_CHANGE_REQUESTS_REQUEST:
      return { ...state, changeRequestsLoader: true, changeRequestsError: null }
    case types.GET_CHANGE_REQUESTS_SUCCESS:
      return {
        ...state,
        changeRequestsLoader: false,
        changeRequests: action.payload || []
      }
    case types.GET_CHANGE_REQUESTS_FAILURE:
      return {
        ...state,
        changeRequestsLoader: false,
        changeRequestsError: action.payload
      }
    case types.RESOLVE_CHANGE_REQUEST_REQUEST:
      return {
        ...state,
        changeRequestActionLoader: true,
        changeRequestActionError: null,
        changeRequestActionSuccess: null
      }
    case types.RESOLVE_CHANGE_REQUEST_SUCCESS:
      return {
        ...state,
        changeRequestActionLoader: false,
        changeRequestActionSuccess: action.payload || 'done'
      }
    case types.RESOLVE_CHANGE_REQUEST_FAILURE:
      return {
        ...state,
        changeRequestActionLoader: false,
        changeRequestActionError: action.payload
      }
    case types.RESET_CHANGE_REQUEST_FEEDBACK:
      return {
        ...state,
        changeRequestActionSuccess: null,
        changeRequestActionError: null
      }

    case types.USER_LOGOUT:
      return initialState

    default:
      return state
  }
}
